// =============================================================================
// Origin UI — src/services/lieuApi.ts
// Day-in-lieu claims API (Oct 2026).
// =============================================================================

import { api } from './apiService';

export type LieuClaimStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled' | 'Revoked';

export interface LieuClaimEvent {
  eventType: string;
  actorName: string;
  comment: string | null;
  createdAt: string;
}

export interface LieuClaim {
  lieuDayClaimId: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  department: string;
  officeLocation: string;
  workedDate: string;
  workedDateLabel: string | null;
  days: number;
  reason: string;
  status: LieuClaimStatus;
  statusLabel: string;

  managerApprovalRequired: boolean;
  managerEmployeeId: string | null;
  managerName: string | null;
  managerDecision: string | null;
  managerDecisionById: string | null;
  managerDecisionByName: string | null;
  managerDecisionAt: string | null;

  hrApprovalRequired: boolean;
  hrDecision: string | null;
  hrDecisionById: string | null;
  hrDecisionByName: string | null;
  hrDecisionAt: string | null;

  decisionComments: string | null;
  approvedAt: string | null;
  creditYear: number | null;
  rejectedAt: string | null;
  rejectedByName: string | null;
  cancellationReason: string | null;
  resubmissionCount: number;
  submittedAt: string;
  createdAt: string;

  canApprove: boolean;
  canReject: boolean;
  canResubmit: boolean;
  canCancel: boolean;
  canRevoke: boolean;
  myApprovalRole: string | null;

  events: LieuClaimEvent[];
}

export interface LieuDateCheck {
  isClaimable: boolean;
  halfDayOnly: boolean;
  message: string;
}

export interface LieuClaimInput {
  workedDate: string;   // yyyy-MM-dd
  isHalfDay: boolean;
  reason: string;
}

export const lieuApi = {
  getMine: () => api.get<LieuClaim[]>('/api/leave/lieu-claims/mine'),

  get: (id: string) => api.get<LieuClaim>(`/api/leave/lieu-claims/${id}`),

  checkDate: (date: string, excludeId?: string) =>
    api.get<LieuDateCheck>(`/api/leave/lieu-claims/check-date?date=${date}${excludeId ? `&excludeId=${excludeId}` : ''}`),

  create: (data: LieuClaimInput) => api.post<LieuClaim>('/api/leave/lieu-claims', data),

  resubmit: (id: string, data: LieuClaimInput & { resubmissionNote?: string }) =>
    api.post<LieuClaim>(`/api/leave/lieu-claims/${id}/resubmit`, data),

  cancel: (id: string, reason?: string) =>
    api.post<LieuClaim>(`/api/leave/lieu-claims/${id}/cancel`, { reason }),

  getPending: () => api.get<LieuClaim[]>('/api/leave/lieu-claims/pending'),

  approve: (id: string, comments?: string) =>
    api.post<LieuClaim>(`/api/leave/lieu-claims/${id}/approve`, { comments }),

  reject: (id: string, comments: string) =>
    api.post<LieuClaim>(`/api/leave/lieu-claims/${id}/reject`, { comments }),

  revoke: (id: string, comments: string) =>
    api.post<LieuClaim>(`/api/leave/lieu-claims/${id}/revoke`, { comments }),

  getAll: (status?: string, year?: number) => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (year) p.set('year', String(year));
    const qs = p.toString();
    return api.get<LieuClaim[]>(`/api/leave/lieu-claims/all${qs ? `?${qs}` : ''}`);
  },
};

// ─── Display helpers shared by the claim pages ───

export function formatLieuDate(iso: string, withWeekday = true): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    ...(withWeekday ? { weekday: 'short' as const } : {}),
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export function formatLieuDays(days: number): string {
  return days === 0.5 ? 'Half day' : days === 1 ? '1 day' : `${days} days`;
}

export const LIEU_EVENT_LABELS: Record<string, string> = {
  Submitted: 'Submitted',
  Resubmitted: 'Resubmitted with more information',
  ManagerApproved: 'Approved by line manager',
  HrApproved: 'Approved by HR',
  ManagerAndHrApproved: 'Approved (line manager & HR)',
  Approved: 'Fully approved — credited to TOIL',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled',
  Revoked: 'Revoked by HR',
};

/** Colour tokens for a claim status pill. */
export function lieuStatusTone(c: Pick<LieuClaim, 'status'>): { bg: string; fg: string; dot: string } {
  switch (c.status) {
    case 'Approved': return { bg: '#E6F7F0', fg: '#00794F', dot: '#00AA6E' };
    case 'Rejected': return { bg: '#FDECEA', fg: '#B42318', dot: '#F04B41' };
    case 'Revoked': return { bg: '#FDECEA', fg: '#B42318', dot: '#F04B41' };
    case 'Cancelled': return { bg: '#F1F4F6', fg: '#4A5568', dot: '#9BA5B0' };
    default: return { bg: '#FFF3E0', fg: '#B45309', dot: '#FF8300' };
  }
}
